import { useQuery } from '@tanstack/react-query';
import { hasMonduSupplier, selectMonduLendingOpportunity } from '@/enterprise/data/monduOpportunitySelectors';
import { fetchUserLendingOpportunities } from '@/enterprise/data/useFundboxOpportunity';
import { fetchTerms, QUERY_KEY_USER_TERMS, SEATerms } from '@/enterprise/data/useTerms';
import { isImpersonationSession } from '@/utils/token';

export const MONDU_LENDING_OPPORTUNITIES_QUERY_KEY = ['lending-opportunities', 'mondu'] as const;

interface UseMonduHandoffSkipResult {
  canSkipModal: boolean;
}

/**
 * Whether the Mondu disclosure modal can be skipped for this supplier.
 *
 * Skipping is only legitimate when the supplier has acknowledged the currently effective
 * `MONDU_DATA_SHARING_CONSENT` document — pressing Proceed in the modal is that acknowledgement —
 * and Mondu has already created a supplier for them. `GET /user/terms` returns the latest effective
 * document per kind, so a new terms version arrives unacknowledged and sends them back to the modal.
 *
 * Deliberately fail-closed: pending, errored, and absent data all resolve to `false`. An unnecessary
 * modal costs a click (SEA no-ops an ack it already holds); an unnecessary skip would bypass a
 * consent the supplier still owes. Both queries are non-suspense so the marketing page keeps
 * rendering immediately and a failing GET degrades to the modal instead of erroring the page.
 */
const useMonduHandoffSkip = (): UseMonduHandoffSkipResult => {
  const { data: hasAcknowledgedConsent } = useQuery({
    queryKey: [QUERY_KEY_USER_TERMS],
    queryFn: fetchTerms,
    throwOnError: false,
    select: (terms: SEATerms) =>
      terms.some((term) => term?.kind === 'MONDU_DATA_SHARING_CONSENT' && term.hasAcknowledged),
  });

  const { data: hasSupplier } = useQuery({
    queryKey: MONDU_LENDING_OPPORTUNITIES_QUERY_KEY,
    queryFn: fetchUserLendingOpportunities,
    throwOnError: false,
    select: (data) => hasMonduSupplier(selectMonduLendingOpportunity(data.opportunities)),
  });

  return {
    canSkipModal: Boolean(hasAcknowledgedConsent) && Boolean(hasSupplier) && !isImpersonationSession(),
  };
};

export default useMonduHandoffSkip;
